/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { NotificationAddOutlined, Speed } from "@mui/icons-material";
import { Alert, Box, Button, Card } from "@mui/material";
import { useAtom } from "jotai";
import { Suspense } from "react";
import ReactQuillViewer from "../../../components/core/ReactQuill/ReactQuillViewer";
import { CardActionBar, CardTitle } from "../../../components/elements";
import { useGlobalOrganizationContext } from "../../../hooks/useGlobalOrganizationContext";
import { Process } from "../../../model/Process";
import { publicPageAtom } from "../../organization/state/publicPageAtom";
import {
  Task,
  TaskBody,
  TaskExecution,
  TaskExecutionRenderProps,
  TaskExecutionState,
} from "../../task";
import {
  getDescription,
  getTitle,
} from "../../task/components/task-types/getTaskByType";

export type SingleTaskExecutionCardProps = {
  taskExecution?: TaskExecution;
  content?: TaskExecutionRenderProps;
  process?: Process;
  task?: Task;
  icon?: string;
  hasNext?: boolean;
  backTitle?: string;
  onExecute: (taskEx: TaskExecution) => Promise<void>;
  onBack?: () => void;
  onNext?: () => void;
  onValueChange?: (task: Task) => void;
  loaded: boolean;
};

const SingleTaskExecutionCard = ({
  taskExecution,
  content,
  process,
  task,
  icon,
  hasNext = false,
  onExecute,
  backTitle,
  onBack,
  onNext = () => {
    /**/
  },
  onValueChange = () => {
    /**/
  },
}: SingleTaskExecutionCardProps) => {
  const { organization } = useGlobalOrganizationContext();
  const [isPublicPage] = useAtom(publicPageAtom);
  const currentTask = task ?? taskExecution?.task;
  const showNextButton =
    taskExecution?.state === TaskExecutionState.Completed && hasNext;
  const showBackButton = !!onBack;
  const taskTitle =
    currentTask && process
      ? getTitle(organization!, process, currentTask, isPublicPage)
      : currentTask?.name;
  const description =
    currentTask && process
      ? getDescription(
          organization!,
          process,
          currentTask,
          taskExecution,
          isPublicPage,
        )
      : currentTask?.description;
  const renderTaskCardContent = ({
    actions,
    title,
    content,
  }: TaskExecutionRenderProps) => {
    const contentArr = content instanceof Array ? content : [content];
    return (
      <>
        <Box
          sx={{
            py: 3,
            px: 4,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
            gap: 2,
          }}
        >
          <CardTitle variant="h2" component="h1" icon={icon}>
            {title ?? taskTitle}
          </CardTitle>
          <Box sx={{ overflow: "auto", flexGrow: 0, pr: 1.5 }}>
            {description && (
              <ReactQuillViewer
                value={description}
                variant="regular"
                collapseHeight={-1}
              />
            )}
          </Box>

          {contentArr.map((element, index) => (
            <Box
              key={index}
              sx={{
                flexGrow: index === contentArr.length - 1 ? 1 : 0,
                display: "flex",
              }}
            >
              {element}
            </Box>
          ))}
        </Box>
        {(actions || showBackButton || showNextButton) && (
          <CardActionBar
            align="space-between"
            sx={{ px: 4, py: 2, bgcolor: "common.white" }}
          >
            {showBackButton && (
              <Button variant="text" onClick={onBack}>
                {backTitle ?? "< Back"}
              </Button>
            )}
            {!showBackButton && <Box sx={{ flex: 1 }}></Box>}
            {taskExecution?.state !== TaskExecutionState.Completed && actions}
            {showNextButton && (
              <Button onClick={onNext} color="primary">
                Next &gt;
              </Button>
            )}
          </CardActionBar>
        )}
      </>
    );
  };
  return (
    <Suspense>
      <Card
        elevation={1}
        sx={{
          display: "flex",
          flexDirection: "column",
          maxHeight: "100%",
          minHeight: "60%",
        }}
      >
        {!content && (
          <TaskBody
            variant="execution"
            view="expanded"
            task={currentTask!}
            taskExecution={taskExecution}
            onValueChange={onValueChange}
            onExecute={onExecute}
            isShortTaskForm={true}
            reload={function (): void {
              throw new Error("Function not implemented.");
            }}
            overrideEditable={
              taskExecution?.state !== TaskExecutionState.Completed
            }
          >
            {renderTaskCardContent}
          </TaskBody>
        )}
        {currentTask?.data?.notifyProcessOwner && (
          <Alert
            icon={<NotificationAddOutlined />}
            severity="info"
            sx={{ width: "100%" }}
          >
            This task will notify the process owner when completed.
          </Alert>
        )}
        {currentTask?.data?.requestTracking && (
          <Alert icon={<Speed />} severity="warning" sx={{ width: "100%" }}>
            This task&apos;s progress is currently being tracked
          </Alert>
        )}
        {content && renderTaskCardContent(content)}
      </Card>
    </Suspense>
  );
};
export default SingleTaskExecutionCard;
